import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import { API } from "../utils/api";

export default function ForgotPasswordPage() {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Error al procesar la solicitud.");
        return;
      }
      setSent(true);
    } catch {
      setError("Error de conexión. Verificá tu internet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
      {/* Video fondo */}
      <video autoPlay muted loop playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
        style={{ opacity: 0.35 }}
      >
        <source
          src="https://customer-assets.emergentagent.com/job_5c81eca5-82cd-4071-a85b-2346e52a1b97/artifacts/jh39v1rk_Video_de_Fa%CC%81brica_de_Pa%CC%81ginas_Web%20%281%29.mp4"
          type="video/mp4"
        />
      </video>
      <div className="absolute inset-0 bg-black/80 z-0 pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm animate-fadeInUp">

        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="Sitios YA" className="h-32 w-auto object-contain mb-5 drop-shadow-2xl" />
          <p className="text-sm text-gray-400 tracking-wide">Recuperar contraseña</p>
        </div>

        {/* Card */}
        <div className="bg-[#161616] border border-white/10 rounded-3xl p-6 shadow-2xl">

          {sent ? (
            /* ── Success state ── */
            <div className="flex flex-col items-center text-center gap-4 py-4" data-testid="forgot-password-success">
              <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-green-400" />
              </div>
              <div>
                <p className="text-white font-black text-lg mb-2">¡Email enviado!</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Si <span className="text-[#ffc700] font-semibold">{email}</span> está registrado,
                  recibirás un enlace para crear una nueva contraseña en los próximos minutos.
                </p>
                <p className="text-gray-600 text-xs mt-3">El enlace expira en 60 minutos.</p>
              </div>
              <Link
                to="/login"
                className="mt-2 flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={14} /> Volver al login
              </Link>
            </div>
          ) : (
            /* ── Form state ── */
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="mb-1">
                <p className="text-white font-black text-lg mb-1">¿Olvidaste tu contraseña?</p>
                <p className="text-gray-400 text-sm">
                  Ingresá tu email y te enviamos un enlace para crear una nueva.
                </p>
              </div>

              {error && (
                <div
                  data-testid="forgot-password-error"
                  className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl"
                >
                  {error}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-400 block mb-1.5 uppercase tracking-wide">
                  Email
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                  <input
                    data-testid="forgot-password-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    autoComplete="email"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50 focus:bg-white/8 transition-colors"
                  />
                </div>
              </div>

              <button
                data-testid="forgot-password-submit-btn"
                type="submit"
                disabled={loading || !email.trim()}
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#ffc700] text-black rounded-2xl font-black text-sm hover:bg-[#e6b300] transition-colors disabled:opacity-60 mt-1 tracking-wide"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Enviando..." : "Enviar enlace"}
              </button>
            </form>
          )}
        </div>

        {/* Link volver */}
        {!sent && (
          <div className="flex justify-center mt-5">
            <Link
              to="/login"
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              <ArrowLeft size={12} /> Volver al inicio de sesión
            </Link>
          </div>
        )}

        <p className="text-center text-xs text-gray-600 mt-5">
          Sitios YA &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
